import AboutTemplate from "../../components/about/AboutTemplate";
import Page from "../Page";
import { Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Section from "../../components/about/Section";
import DeveloperNotes from "../../components/about/DeveloperNotes";
import ResponsiveImage from "../../components/about/ResponsiveImage";

const TaskSorting = () => {
  return (
    <Page className="about-page task-sorting-page" pageKey="task-sorting">
      <AboutTemplate>
        <Typography variant="h1">Task Sorting</Typography>

        <Section>
          <Typography variant="p1">
            Task sorting controls the order in which your tasks appear in your
            stack. By choosing a sort method, you can ensure the tasks that
            matter most to you right now are always at the top.
          </Typography>
          <ResponsiveImage
            pageKey="taskSorting"
            imageName="sort-chip"
            size="large"
          />
        </Section>

        <Section title="Changing your sort method">
          <Typography variant="p1">
            To change how your tasks are sorted, click the sort chip at the top
            of your task stack. This will open the Sort Tasks dialog where you
            can choose from a variety of sort options.
          </Typography>
          <ResponsiveImage
            pageKey="taskSorting"
            imageName="sort-dialog"
            size="medium"
          />
        </Section>

        <Section title="Adaptive sorting">
          <Typography variant="p1">
            <strong>Adaptive</strong> is the default and recommended sort
            method. It uses an intelligent algorithm that takes into account
            your current{" "}
            <Link
              component={RouterLink}
              to="/about/features/mindset"
              variant="p1Link"
            >
              mindset
            </Link>{" "}
            (mood and energy), each task's{" "}
            <Link
              component={RouterLink}
              to="/about/features/tasks/attributes"
              variant="p1Link"
            >
              attributes
            </Link>{" "}
            (importance, difficulty, and fun), and deadline urgency to
            dynamically order your tasks.
          </Typography>
          <Typography variant="p1">
            When you're feeling low on energy, easier tasks will float to the
            top. When you're feeling great, more important and challenging tasks
            will take priority. Approaching deadlines will always push urgent
            tasks higher.
          </Typography>
          <ResponsiveImage
            pageKey="taskSorting"
            imageName="adaptive-sort"
            size="large"
          />
        </Section>

        <Section title="Manual sort options">
          <Typography variant="p1">
            If you prefer more control, you can sort your tasks by a single
            attribute. Each option can be sorted in ascending or descending
            order:
          </Typography>
          <Typography variant="p1">
            <strong>Importance</strong> - Sort by how important a task is.
            Choose "Most important" to see critical tasks first, or "Least
            important" for quick wins.
          </Typography>
          <Typography variant="p1">
            <strong>Difficulty</strong> - Sort by how hard a task is. Choose
            "Most difficult" when you have energy to tackle tough tasks, or
            "Least difficult" when you want easy wins.
          </Typography>
          <Typography variant="p1">
            <strong>Fun</strong> - Sort by how enjoyable a task is. Great for
            when you need a motivational boost.
          </Typography>
          <Typography variant="p1">
            <strong>Created date</strong> - Sort by when a task was added.
            "Oldest" can help you tackle long-standing tasks; "Most recent"
            keeps new tasks front of mind.
          </Typography>
          <Typography variant="p1">
            <strong>Deadline</strong> - Sort by deadline. "Soonest deadline"
            ensures you never miss an approaching due date.
          </Typography>
          <Typography variant="p1">
            <strong>Scheduled date</strong> - Sort by when a task is scheduled
            for. Useful for planning ahead.
          </Typography>
          <Typography variant="p1">
            <strong>Name</strong> - Sort alphabetically. Handy for quickly
            finding a task by name.
          </Typography>
        </Section>

        <Section title="Your sort preference is saved">
          <Typography variant="p1">
            Your chosen sort method is saved to your account, so it will persist
            across sessions and devices. You can change it at any time.
          </Typography>
        </Section>

        <DeveloperNotes>
          <Typography variant="p1">
            I'd recommend sticking with Adaptive sorting most of the time - it
            was designed to do the thinking for you, so you spend less time
            deciding what to do and more time actually doing it.
          </Typography>
          <Typography variant="p1">
            That said, manual sort options are useful when you have a specific
            goal in mind. For example, if you know you have a deadline coming
            up, sorting by deadline can bring urgent tasks into sharp focus.
          </Typography>
          <Typography variant="p1">
            The Adaptive sort works best when you've filled in your task
            attributes (importance, difficulty, and fun) and set your mindset -
            the more information it has, the smarter it can be about ordering
            your tasks.
          </Typography>
        </DeveloperNotes>
      </AboutTemplate>
    </Page>
  );
};

export default TaskSorting;
